<div class="scheduled-payment-details">
  <div class="details-header">
    <h2>Scheduled Payment Details</h2>
    <span class="status-badge" [class]="'status-' + payment.status">
      {{ payment.status | uppercase }}
    </span>
  </div>

  <!-- Payment Information -->
  <div class="details-section">
    <h3><mat-icon>payment</mat-icon> Payment Information</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">Plan Type:</span>
        <span class="value">{{ payment.paymentPlanType === 'total_divided' ? 'Payment Plan' : 'Weekly Recurring' }}</span>
      </div>
      <div class="info-item">
        <span class="label">Frequency:</span>
        <span class="value">{{ payment.frequency | titlecase }}</span>
      </div>
      <div class="info-item">
        <span class="label">Total Amount:</span>
        <span class="value">{{ payment.totalAmount | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Weekly Amount:</span>
        <span class="value">{{ payment.calculatedWeeklyAmount | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Number of Weeks:</span>
        <span class="value">{{ payment.numberOfWeeks }}</span>
      </div>
      <div class="info-item" *ngIf="payment.referenceNumber">
        <span class="label">Reference Number:</span>
        <span class="value">{{ payment.referenceNumber }}</span>
      </div>
    </div>
  </div>

  <!-- Capital Cost Information -->
  <div class="details-section" *ngIf="payment.capitalCostEnabled">
    <h3><mat-icon>percent</mat-icon> Capital Cost</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">Capital Cost %:</span>
        <span class="value">{{ payment.capitalCostPercentage }}%</span>
      </div>
      <div class="info-item">
        <span class="label">Capital Cost Amount:</span>
        <span class="value">{{ payment.capitalCostAmount | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Capital Cost per Payment:</span>
        <span class="value">{{ getCapitalCostPerPayment() | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Charged per Payment:</span>
        <span class="value">{{ getChargePerPayment() | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Total with Capital Cost:</span>
        <span class="value highlight">{{ getTotalWithCapitalCost() | currency }}</span>
      </div>
    </div>
  </div>

  <!-- Card Information -->
  <div class="details-section">
    <h3><mat-icon>credit_card</mat-icon> Payment Method</h3>
    <div class="card-display" *ngIf="cardInfo">
      <mat-icon>credit_card</mat-icon>
      <div class="card-info">
        <div class="card-number">{{ cardInfo.accountType }} ending in {{ getFormattedCardNumber() }}</div>
        <div class="card-meta">
          <span>Exp: {{ getFormattedExpiration() }}</span>
          <span *ngIf="cardInfo.cardHolderName">•</span>
          <span *ngIf="cardInfo.cardHolderName">{{ cardInfo.cardHolderName }}</span>
        </div>
      </div>
    </div>
    <div class="card-display" *ngIf="!cardInfo">
      <mat-icon>credit_card</mat-icon>
      <div class="card-info">
        <div class="card-number">Loading card information...</div>
      </div>
    </div>
  </div>

  <!-- Schedule Information -->
  <div class="details-section">
    <h3><mat-icon>event</mat-icon> Schedule</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">Start Date:</span>
        <span class="value">{{ payment.startDate | date:'mediumDate' }}</span>
      </div>
      <div class="info-item">
        <span class="label">End Date:</span>
        <span class="value">{{ payment.endDate | date:'mediumDate' }}</span>
      </div>
      <div class="info-item" *ngIf="payment.nextExecutionDate && payment.status === 'active'">
        <span class="label">Next Payment:</span>
        <span class="value highlight">{{ payment.nextExecutionDate | date:'mediumDate' }}</span>
      </div>
      <div class="info-item" *ngIf="payment.lastExecutionDate">
        <span class="label">Last Payment:</span>
        <span class="value">{{ payment.lastExecutionDate | date:'mediumDate' }}</span>
      </div>
    </div>
  </div>

  <!-- Progress Information -->
  <div class="details-section">
    <h3><mat-icon>analytics</mat-icon> Progress</h3>
    <div class="info-grid">
      <div class="info-item">
        <span class="label">Payments Made:</span>
        <span class="value">{{ payment.executionCount }} / {{ payment.numberOfWeeks }}</span>
      </div>
      <div class="info-item">
        <span class="label">Remaining Payments:</span>
        <span class="value">{{ payment.remainingPayments }}</span>
      </div>
      <div class="info-item" *ngIf="payment.skippedPayments">
        <span class="label">Skipped Payments:</span>
        <span class="value">{{ payment.skippedPayments }}</span>
      </div>
      <div class="info-item" *ngIf="payment.failedPayments">
        <span class="label">Failed Attempts:</span>
        <span class="value error">{{ payment.failedPayments }}</span>
      </div>
      <div class="info-item">
        <span class="label">Total Paid:</span>
        <span class="value success">{{ payment.totalPaid | currency }}</span>
      </div>
      <div class="info-item">
        <span class="label">Remaining Balance:</span>
        <span class="value">{{ payment.remainingBalance | currency }}</span>
      </div>
    </div>
  </div>

  <!-- Error/Warning Information (if failed attempts exist) -->
  <div class="details-section warning-section" *ngIf="payment.status === 'active' && payment.failedPayments && payment.failedPayments > 0">
    <h3><mat-icon color="warn">warning</mat-icon> Payment Retry Status</h3>
    <div class="warning-content">
      <p><strong>⚠️ This payment has {{ payment.failedPayments }} failed attempt(s)</strong></p>
      <p>The system will automatically retry on the next scheduled payment cycle ({{ payment.failedPayments }}/3 attempts).</p>
      <p *ngIf="payment.lastErrorMessage" class="error-message">
        <strong>Last Error:</strong> {{ payment.lastErrorMessage }}
      </p>
    </div>
  </div>

  <!-- Failed Payment Warning (if status is failed) -->
  <div class="details-section error-section" *ngIf="payment.status === 'failed'">
    <h3><mat-icon color="warn">error</mat-icon> Payment Failed</h3>
    <div class="error-content">
      <p><strong>❌ Maximum retry attempts exceeded (3/3)</strong></p>
      <p>This scheduled payment has failed and requires attention. Please check the error details below and update the payment method or contact support.</p>
      <p *ngIf="payment.lastErrorMessage" class="error-message">
        <strong>Error Details:</strong> {{ payment.lastErrorMessage }}
      </p>
    </div>
  </div>

  <!-- Description -->
  <div class="details-section" *ngIf="payment.description">
    <h3><mat-icon>description</mat-icon> Description / Notes</h3>
    <p class="description-text">{{ payment.description }}</p>
  </div>

  <!-- Transaction History -->
  <div class="details-section">
    <h3><mat-icon>history</mat-icon> Payment History</h3>
    
    <!-- Next Scheduled Payment (if active) -->
    <div class="transaction-history" *ngIf="payment.status === 'active' && payment.nextExecutionDate">
      <div class="history-item">
        <div class="history-icon">
          <mat-icon class="pending">schedule</mat-icon>
        </div>
        <div class="history-content">
          <div class="history-title">
            Next Payment
            <span class="status-chip status-pending">Scheduled</span>
          </div>
          <div class="history-meta">
            <span><mat-icon>event</mat-icon> {{ payment.nextExecutionDate | date:'medium' }}</span>
            <span><mat-icon>attach_money</mat-icon> {{ payment.calculatedWeeklyAmount | currency }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Actual payment history (executed payments only) -->
    <div class="transaction-history" *ngIf="actualPaymentHistory.length > 0">
      <div class="history-item" *ngFor="let transaction of actualPaymentHistory; let i = index">
        <div class="history-icon">
          <mat-icon [class]="transaction.status === 'success' ? 'success' : 'error'">
            {{ transaction.status === 'success' ? 'check_circle' : 'error' }}
          </mat-icon>
        </div>
        <div class="history-content">
          <div class="history-title">
            Payment #{{ i + 1 }}
            <span class="status-chip" [class]="'status-' + transaction.status">
              {{ transaction.status | titlecase }}
            </span>
          </div>
          <div class="history-meta">
            <span><mat-icon>event</mat-icon> {{ transaction.executionDate | date:'medium' }}</span>
            <span><mat-icon>attach_money</mat-icon> {{ transaction.amount | currency }}</span>
            <span *ngIf="transaction.transactionId">
              <mat-icon>receipt</mat-icon> {{ transaction.transactionId }}
            </span>
          </div>
          <div class="history-note" *ngIf="transaction.notes">
            {{ transaction.notes }}
          </div>
        </div>
      </div>
    </div>

    <!-- No history message -->
    <div class="no-history" *ngIf="actualPaymentHistory.length === 0 && (!payment.status || payment.status !== 'active' || !payment.nextExecutionDate)">
      <mat-icon>info</mat-icon>
      <p>No payments have been executed yet.</p>
    </div>
  </div>
</div>